// mindmetal-app/web/src/components/mindmetal/SignalGauge.tsx
import { motion } from 'framer-motion'
import { cn } from '@personic/shared'
import type { TrackedValue } from '@personic/shared'

interface SignalGaugeProps {
  label: string
  value: TrackedValue | undefined
  leftLabel?: string
  rightLabel?: string
  className?: string
}

export function SignalGauge({
  label,
  value,
  leftLabel = 'Low',
  rightLabel = 'High',
  className,
}: SignalGaugeProps) {
  const val = value?.value ?? 0
  const confidence = value?.confidence ?? 0

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-midnight-800">{label}</span>
        <span className="text-warm-500">
          {(val * 100).toFixed(0)}%
          {confidence > 0 && (
            <span className="text-xs ml-1">(±{((1 - confidence) * 100).toFixed(0)}%)</span>
          )}
        </span>
      </div>

      <div className="relative h-2 bg-warm-100 rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-copper-400 to-copper-600 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${val * 100}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
        {/* Confidence indicator */}
        {confidence > 0 && (
          <motion.div
            className="absolute inset-y-0 bg-copper-300/50 rounded-full"
            initial={{ left: 0, width: 0 }}
            animate={{
              left: `${Math.max(0, val - (1 - confidence) * 0.1) * 100}%`,
              width: `${(1 - confidence) * 20}%`,
            }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
          />
        )}
      </div>

      <div className="flex justify-between text-xs text-warm-400">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
    </div>
  )
}
